import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  CircuitBoard,
  Move,
  Plus,
  RefreshCcw,
  Save,
  Search,
  Trash2,
} from 'lucide-react';
import { adminBoardsAPI } from '../services/adminApi';
import { prepareInlineSvgMarkup } from '../utils/inlineSvg';

const SVG_LENGTH_TO_PX = {
  px: 1,
  in: 96,
  mm: 96 / 25.4,
  cm: 96 / 2.54,
  pt: 96 / 72,
  pc: 16,
  q: 96 / 101.6,
};

const PREVIEW_SVG_CSS = `
  [data-micropinouts-callout="1"],
  [data-micropinouts-callout-style="1"],
  .pin-hole,
  .pin-rect {
    display: none !important;
  }

  svg {
    overflow: visible !important;
  }
`;

const DEFAULT_TARGET_WIDTH = 260;
const SCALE_MIN = 0.25;
const SCALE_MAX = 3;
const SCALE_STEP = 0.05;

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  align-items: start;
`;

const Panel = styled.section`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 18px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
`;

const PanelTitle = styled.h3`
  margin: 0 0 10px 0;
  color: #111827;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PanelText = styled.p`
  margin: 0 0 14px 0;
  color: #64748b;
  font-size: 0.9rem;
  line-height: 1.55;
`;

const SearchWrap = styled.label`
  position: relative;
  display: block;
  margin-bottom: 14px;
`;

const SearchIcon = styled(Search)`
  position: absolute;
  top: 50%;
  left: 12px;
  transform: translateY(-50%);
  color: #94a3b8;
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 12px 10px 38px;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  font-size: 0.92rem;

  &:focus {
    outline: none;
    border-color: #2c9fa4;
    box-shadow: 0 0 0 3px rgba(44, 159, 164, 0.14);
  }
`;

const BoardList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 12px;
  max-height: 360px;
  overflow-y: auto;
  padding-right: 4px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    max-height: 420px;
  }
`;

const BoardListItem = styled.button`
  width: 100%;
  border: 1px solid ${props => props.$active ? '#7dd3fc' : '#e5e7eb'};
  background: ${props => props.$active ? '#f0f9ff' : '#ffffff'};
  border-radius: 12px;
  padding: 10px;
  min-height: 74px;
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  text-align: left;
  cursor: pointer;

  &:hover {
    border-color: #7dd3fc;
    background: #f8fafc;
  }
`;

const BoardThumb = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 100% !important;
    height: 100% !important;
    display: block;
  }
`;

const BoardThumbSvg = styled.div`
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

const BoardText = styled.div`
  min-width: 0;
`;

const BoardName = styled.div`
  color: #111827;
  font-size: 0.92rem;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BoardMeta = styled.div`
  margin-top: 3px;
  color: #6b7280;
  font-size: 0.78rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BoardScaleTag = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 56px;
  padding: 5px 8px;
  border-radius: 999px;
  background: #eef2ff;
  color: #4338ca;
  font-size: 0.72rem;
  font-weight: 800;
`;

const CanvasPanel = styled(Panel)`
  padding: 16px;
`;

const CanvasHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;

  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

const CanvasMeta = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const MetaBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
  border-radius: 999px;
  background: ${props => props.$tone === 'warning' ? '#fff7ed' : '#eff6ff'};
  color: ${props => props.$tone === 'warning' ? '#c2410c' : '#1d4ed8'};
  font-size: 0.76rem;
  font-weight: 800;
`;

const CanvasStage = styled.div`
  position: relative;
  width: 100%;
  min-height: 700px;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #dbeafe;
  background:
    linear-gradient(rgba(37, 99, 235, 0.08) 1px, transparent 1px),
    linear-gradient(90deg, rgba(37, 99, 235, 0.08) 1px, transparent 1px),
    linear-gradient(180deg, #f8fbff 0%, #eef6ff 100%);
  background-size: 24px 24px, 24px 24px, 100% 100%;

  @media (max-width: 768px) {
    min-height: 560px;
  }
`;

const CanvasBoard = styled.button`
  position: absolute;
  left: ${props => props.$x}px;
  top: ${props => props.$y}px;
  width: ${props => props.$width}px;
  height: ${props => props.$height}px;
  border: 1px solid ${props => props.$selected ? '#2563eb' : '#bfdbfe'};
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.92);
  padding: 14px 14px 30px;
  box-shadow: ${props => props.$selected ? '0 16px 36px rgba(37, 99, 235, 0.18)' : '0 10px 24px rgba(15, 23, 42, 0.10)'};
  cursor: grab;
  overflow: visible;
  user-select: none;
  touch-action: none;

  &:active {
    cursor: grabbing;
  }
`;

const CanvasBoardLabel = styled.div`
  position: absolute;
  left: 14px;
  right: 14px;
  bottom: 8px;
  color: #0f172a;
  font-size: 0.75rem;
  font-weight: 700;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CanvasBoardSvg = styled.div`
  width: 100%;
  height: 100%;
  pointer-events: none;

  > svg {
    width: 100% !important;
    height: 100% !important;
    display: block !important;
  }
`;

const CanvasBoardPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 12px;
  border: 1px dashed #93c5fd;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2563eb;
  font-size: 0.85rem;
  font-weight: 700;
  text-align: center;
  padding: 10px;
`;

const EmptyCanvas = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const EmptyCanvasCard = styled.div`
  max-width: 420px;
  padding: 26px 24px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid #dbeafe;
  color: #475569;
  text-align: center;
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.08);
`;

const EmptyTitle = styled.h4`
  margin: 0 0 8px 0;
  color: #0f172a;
  font-size: 1rem;
`;

const EmptyText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.6;
`;

const InspectorValue = styled.div`
  color: #111827;
  font-size: 1rem;
  font-weight: 800;
  margin-bottom: 8px;
`;

const RangeInput = styled.input`
  width: 100%;
  accent-color: #2c9fa4;
`;

const NumberInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  font-size: 0.92rem;

  &:focus {
    outline: none;
    border-color: #2c9fa4;
    box-shadow: 0 0 0 3px rgba(44, 159, 164, 0.14);
  }
`;

const CheckboxField = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px;
  border: 1px solid #dbe4ee;
  border-radius: 12px;
  background: #f8fafc;
  cursor: pointer;
`;

const CheckboxInput = styled.input`
  width: 18px;
  height: 18px;
  margin: 2px 0 0;
  accent-color: #2c9fa4;
  flex-shrink: 0;
`;

const CheckboxText = styled.div`
  color: #0f172a;
  font-size: 0.9rem;
  font-weight: 700;
`;

const CheckboxHint = styled.div`
  margin-top: 4px;
  color: #64748b;
  font-size: 0.8rem;
  line-height: 1.5;
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
`;

const Button = styled.button`
  border: none;
  border-radius: 10px;
  padding: 10px 14px;
  background: ${props => props.$primary ? '#2c9fa4' : '#eef2f7'};
  color: ${props => props.$primary ? '#ffffff' : '#334155'};
  font-size: 0.84rem;
  font-weight: 800;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background: ${props => props.$primary ? '#1c6568' : '#e2e8f0'};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const FieldLabel = styled.label`
  display: block;
  color: #334155;
  font-size: 0.82rem;
  font-weight: 700;
  margin: 14px 0 8px;
`;

const InlineInfo = styled.div`
  margin-top: 8px;
  color: #64748b;
  font-size: 0.82rem;
  line-height: 1.55;
`;

const StatusMessage = styled.div`
  margin-top: 14px;
  padding: 10px 12px;
  border-radius: 10px;
  background: ${props => props.$error ? '#fef2f2' : '#f0fdf4'};
  color: ${props => props.$error ? '#b91c1c' : '#166534'};
  border: 1px solid ${props => props.$error ? '#fecaca' : '#bbf7d0'};
  font-size: 0.84rem;
  line-height: 1.5;
`;

const EmptyList = styled.div`
  padding: 16px 12px;
  border: 1px dashed #d1d5db;
  border-radius: 12px;
  color: #6b7280;
  font-size: 0.88rem;
  text-align: center;
`;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const clampScale = (value) => {
  const numeric = Number(value);

  if (!Number.isFinite(numeric)) {
    return 1;
  }

  return Number(clamp(numeric, SCALE_MIN, SCALE_MAX).toFixed(2));
};

const getRenderedSvgContent = (board) => String(
  board?.svg_content ||
  board?.svgContent ||
  ''
).trim();

const getSourceSvgContent = (board) => String(
  board?.svg_source_content ||
  board?.svgSourceContent ||
  ''
).trim();

const getBoardProjectBuilderScale = (board) => {
  const scaleValue = Number(
    board?.project_builder_scale ??
    board?.projectBuilderScale ??
    1
  );

  return Number.isFinite(scaleValue) ? clampScale(scaleValue) : 1;
};

const getBoardProjectBuilderEnabled = (board) => {
  const enabledValue = board?.project_builder_enabled ?? board?.projectBuilderEnabled;

  if (enabledValue === undefined || enabledValue === null || enabledValue === '') {
    return true;
  }

  if (enabledValue === false || enabledValue === 0 || enabledValue === '0') {
    return false;
  }

  if (typeof enabledValue === 'string') {
    return !['false', 'no', 'off'].includes(enabledValue.trim().toLowerCase());
  }

  return true;
};

const parseSvgLength = (value) => {
  const text = String(value || '').trim();

  if (!text) {
    return null;
  }

  const match = text.match(/^([+-]?\d*\.?\d+)([a-z%]*)$/i);
  if (!match) {
    return null;
  }

  const amount = Number.parseFloat(match[1]);
  const unit = String(match[2] || 'px').toLowerCase();

  if (!Number.isFinite(amount)) {
    return null;
  }

  return {
    value: amount,
    unit,
    px: SVG_LENGTH_TO_PX[unit] ? amount * SVG_LENGTH_TO_PX[unit] : null,
  };
};

const parseViewBox = (value) => {
  const parts = String(value || '')
    .trim()
    .split(/[\s,]+/)
    .map((segment) => Number.parseFloat(segment))
    .filter((segment) => Number.isFinite(segment));

  if (parts.length >= 4) {
    return {
      width: parts[2],
      height: parts[3],
    };
  }

  return null;
};

const parseSvgMetrics = (rawSvgContent) => {
  const fallback = {
    width: DEFAULT_TARGET_WIDTH,
    height: 180,
  };

  const markup = String(rawSvgContent || '').trim();
  if (!markup) {
    return fallback;
  }

  let widthValue = '';
  let heightValue = '';
  let viewBoxValue = '';

  if (typeof window !== 'undefined' && typeof DOMParser !== 'undefined') {
    try {
      const parser = new DOMParser();
      const parsedDocument = parser.parseFromString(markup, 'image/svg+xml');
      const parseError = parsedDocument.querySelector('parsererror');
      const svg = parsedDocument.documentElement;

      if (!parseError && svg && svg.nodeName.toLowerCase().includes('svg')) {
        widthValue = svg.getAttribute('width') || '';
        heightValue = svg.getAttribute('height') || '';
        viewBoxValue = svg.getAttribute('viewBox') || '';
      }
    } catch {
      // Fall back to regex parsing below.
    }
  }

  if (!widthValue || !heightValue) {
    widthValue = widthValue || markup.match(/\bwidth=["']([^"']+)["']/i)?.[1] || '';
    heightValue = heightValue || markup.match(/\bheight=["']([^"']+)["']/i)?.[1] || '';
  }

  if (!viewBoxValue) {
    viewBoxValue = markup.match(/\bviewBox=["']([^"']+)["']/i)?.[1] || '';
  }

  const widthInfo = parseSvgLength(widthValue);
  const heightInfo = parseSvgLength(heightValue);
  const viewBox = parseViewBox(viewBoxValue);

  if (widthInfo?.px && heightInfo?.px) {
    return {
      width: Math.max(widthInfo.px, 1),
      height: Math.max(heightInfo.px, 1),
    };
  }

  if (viewBox && viewBox.width > 0 && viewBox.height > 0) {
    return {
      width: viewBox.width,
      height: viewBox.height,
    };
  }

  if (widthInfo?.value && heightInfo?.value) {
    return {
      width: Math.max(widthInfo.value, 1),
      height: Math.max(heightInfo.value, 1),
    };
  }

  return fallback;
};

const getPreparedBoardSvg = (board) => {
  const svg = getRenderedSvgContent(board) || getSourceSvgContent(board);

  if (!svg) {
    return '';
  }

  return prepareInlineSvgMarkup(svg, {
    extraCssText: PREVIEW_SVG_CSS,
  });
};

const getBoardFootprint = (board, scale) => {
  const svg = getRenderedSvgContent(board) || getSourceSvgContent(board);
  const metric = parseSvgMetrics(svg);
  const aspectRatio = metric.width > 0 ? metric.height / metric.width : 0.7;
  const safeScale = clampScale(scale);

  return {
    width: DEFAULT_TARGET_WIDTH * safeScale,
    height: DEFAULT_TARGET_WIDTH * safeScale * aspectRatio,
  };
};

const formatScalePercent = (scale) => `${Math.round(clampScale(scale) * 100)}%`;

const ProjectBuilderBoardSizer = ({
  boards,
  boardsLoading = false,
  onBoardScaleSaved,
}) => {
  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  const [canvasSize, setCanvasSize] = useState({ width: 920, height: 700 });
  const [searchTerm, setSearchTerm] = useState('');
  const [canvasItems, setCanvasItems] = useState([]);
  const [selectedCanvasItemId, setSelectedCanvasItemId] = useState('');
  const [scaleDrafts, setScaleDrafts] = useState({});
  const [enabledDrafts, setEnabledDrafts] = useState({});
  const [savingBoardIds, setSavingBoardIds] = useState(new Set());
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const selectableBoards = useMemo(
    () => (Array.isArray(boards) ? boards : []),
    [boards]
  );

  const filteredBoards = useMemo(() => {
    const searchValue = searchTerm.trim().toLowerCase();

    if (!searchValue) {
      return selectableBoards;
    }

    return selectableBoards.filter((board) => {
      const name = String(board.name || '').toLowerCase();
      const manufacturer = String(board.manufacturer || '').toLowerCase();
      const category = String(board.category || '').toLowerCase();

      return (
        name.includes(searchValue) ||
        manufacturer.includes(searchValue) ||
        category.includes(searchValue)
      );
    });
  }, [searchTerm, selectableBoards]);

  const selectedCanvasItem = useMemo(
    () => canvasItems.find((item) => item.id === selectedCanvasItemId) || null,
    [canvasItems, selectedCanvasItemId]
  );

  const selectedBoard = useMemo(
    () => selectableBoards.find((board) => String(board.id) === String(selectedCanvasItem?.boardId)) || null,
    [selectableBoards, selectedCanvasItem]
  );

  const boardsById = useMemo(
    () => new Map(selectableBoards.map((board) => [String(board.id), board])),
    [selectableBoards]
  );

  const getBoardById = useCallback(
    (boardId) => boardsById.get(String(boardId)) || null,
    [boardsById]
  );

  const getDraftScale = useCallback((boardId) => {
    const draftValue = scaleDrafts[String(boardId)];

    if (draftValue !== undefined) {
      return clampScale(draftValue);
    }

    return getBoardProjectBuilderScale(getBoardById(boardId));
  }, [getBoardById, scaleDrafts]);

  const getDraftEnabled = useCallback((boardId) => {
    const draftValue = enabledDrafts[String(boardId)];

    if (draftValue !== undefined) {
      return Boolean(draftValue);
    }

    return getBoardProjectBuilderEnabled(getBoardById(boardId));
  }, [enabledDrafts, getBoardById]);

  const dirtyBoardIds = useMemo(() => {
    const nextDirtyIds = [];

    selectableBoards.forEach((board) => {
      const boardId = String(board.id);
      const savedScale = getBoardProjectBuilderScale(board);
      const draftScale = scaleDrafts[boardId] === undefined
        ? savedScale
        : clampScale(scaleDrafts[boardId]);
      const savedEnabled = getBoardProjectBuilderEnabled(board);
      const draftEnabled = enabledDrafts[boardId] === undefined
        ? savedEnabled
        : Boolean(enabledDrafts[boardId]);

      if (
        Math.abs(savedScale - draftScale) > 0.0001 ||
        savedEnabled !== draftEnabled
      ) {
        nextDirtyIds.push(boardId);
      }
    });

    return nextDirtyIds;
  }, [enabledDrafts, scaleDrafts, selectableBoards]);

  useEffect(() => {
    const canvasNode = canvasRef.current;

    if (!canvasNode || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const updateCanvasSize = () => {
      const rect = canvasNode.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setCanvasSize({
          width: rect.width,
          height: rect.height,
        });
      }
    };

    updateCanvasSize();

    const observer = new ResizeObserver(updateCanvasSize);
    observer.observe(canvasNode);
    window.addEventListener('resize', updateCanvasSize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateCanvasSize);
    };
  }, []);

  useEffect(() => {
    const handlePointerMove = (event) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) {
        return;
      }

      const nextX = drag.originX + (event.clientX - drag.startClientX);
      const nextY = drag.originY + (event.clientY - drag.startClientY);
      const maxX = Math.max(canvasSize.width - drag.itemWidth - 12, 12);
      const maxY = Math.max(canvasSize.height - drag.itemHeight - 12, 12);

      setCanvasItems((currentItems) =>
        currentItems.map((item) =>
          item.id === drag.itemId
            ? {
                ...item,
                x: clamp(nextX, 12, maxX),
                y: clamp(nextY, 12, maxY),
              }
            : item
        )
      );
    };

    const handlePointerUp = (event) => {
      if (dragRef.current?.pointerId === event.pointerId) {
        dragRef.current = null;
      }
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [canvasSize.height, canvasSize.width]);

  useEffect(() => {
    setCanvasItems((currentItems) =>
      currentItems.map((item) => {
        const board = getBoardById(item.boardId);
        if (!board) {
          return item;
        }

        const footprint = getBoardFootprint(board, getDraftScale(item.boardId));
        const maxX = Math.max(canvasSize.width - footprint.width - 12, 12);
        const maxY = Math.max(canvasSize.height - footprint.height - 12, 12);

        return {
          ...item,
          x: clamp(item.x, 12, maxX),
          y: clamp(item.y, 12, maxY),
        };
      })
    );
  }, [canvasSize.height, canvasSize.width, getBoardById, getDraftScale]);

  const addBoardToCanvas = (board) => {
    if (!board) {
      return;
    }

    const boardId = String(board.id);
    const scale = getDraftScale(boardId);
    const footprint = getBoardFootprint(board, scale);
    const offsetIndex = canvasItems.length % 6;
    const rawX = 24 + offsetIndex * 36;
    const rawY = 96 + offsetIndex * 28;
    const maxX = Math.max(canvasSize.width - footprint.width - 12, 12);
    const maxY = Math.max(canvasSize.height - footprint.height - 12, 12);
    const itemId = `pb-admin-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    setScaleDrafts((currentDrafts) => (
      currentDrafts[boardId] !== undefined
        ? currentDrafts
        : {
            ...currentDrafts,
            [boardId]: getBoardProjectBuilderScale(board),
          }
    ));
    setEnabledDrafts((currentDrafts) => (
      currentDrafts[boardId] !== undefined
        ? currentDrafts
        : {
            ...currentDrafts,
            [boardId]: getBoardProjectBuilderEnabled(board),
          }
    ));

    setCanvasItems((currentItems) => [
      ...currentItems,
      {
        id: itemId,
        boardId,
        x: clamp(rawX, 12, maxX),
        y: clamp(rawY, 12, maxY),
      },
    ]);
    setSelectedCanvasItemId(itemId);
    setErrorMessage('');
    setStatusMessage('');
  };

  const removeCanvasItem = (itemId) => {
    setCanvasItems((currentItems) => currentItems.filter((item) => item.id !== itemId));

    if (selectedCanvasItemId === itemId) {
      setSelectedCanvasItemId('');
    }
  };

  const updateScaleDraft = (boardId, nextValue) => {
    if (String(nextValue).trim() === '') {
      return;
    }

    const nextScale = clampScale(nextValue);

    setScaleDrafts((currentDrafts) => ({
      ...currentDrafts,
      [String(boardId)]: nextScale,
    }));
    setErrorMessage('');
    setStatusMessage('');
  };

  const updateEnabledDraft = (boardId, nextEnabled) => {
    setEnabledDrafts((currentDrafts) => ({
      ...currentDrafts,
      [String(boardId)]: Boolean(nextEnabled),
    }));
    setErrorMessage('');
    setStatusMessage('');
  };

  const saveBoardScale = async (boardId) => {
    const board = getBoardById(boardId);
    if (!board) {
      return;
    }

    const resolvedBoardId = String(boardId);
    const nextScale = getDraftScale(resolvedBoardId);
    const nextEnabled = getDraftEnabled(resolvedBoardId);

    setSavingBoardIds((currentIds) => {
      const nextIds = new Set(currentIds);
      nextIds.add(resolvedBoardId);
      return nextIds;
    });

    try {
      const response = await adminBoardsAPI.updateProjectBuilder(board.id, {
        project_builder_scale: nextScale,
        project_builder_enabled: nextEnabled,
      });

      const payload = response?.data?.data || response?.data || {};
      const savedScale = clampScale(payload.project_builder_scale ?? nextScale);
      const savedEnabled = getBoardProjectBuilderEnabled({
        project_builder_enabled: payload.project_builder_enabled ?? nextEnabled,
      });

      setScaleDrafts((currentDrafts) => ({
        ...currentDrafts,
        [resolvedBoardId]: savedScale,
      }));
      setEnabledDrafts((currentDrafts) => ({
        ...currentDrafts,
        [resolvedBoardId]: savedEnabled,
      }));
      onBoardScaleSaved?.(board.id, {
        project_builder_scale: savedScale,
        project_builder_enabled: savedEnabled ? 1 : 0,
      });
      setStatusMessage(
        savedEnabled
          ? `${board.name} is available in Builder at ${formatScalePercent(savedScale)}.`
          : `${board.name} is now hidden from Builder.`
      );
      setErrorMessage('');
    } catch (error) {
      const nextMessage = error?.response?.data?.message || error?.message || 'Failed to save Builder settings.';
      setErrorMessage(nextMessage);
      setStatusMessage('');
    } finally {
      setSavingBoardIds((currentIds) => {
        const nextIds = new Set(currentIds);
        nextIds.delete(resolvedBoardId);
        return nextIds;
      });
    }
  };

  const saveAllDirtyBoards = async () => {
    for (const boardId of dirtyBoardIds) {
      // eslint-disable-next-line no-await-in-loop
      await saveBoardScale(boardId);
    }
  };

  const selectedBoardScale = selectedBoard ? getDraftScale(selectedBoard.id) : 1;
  const selectedBoardEnabled = selectedBoard ? getDraftEnabled(selectedBoard.id) : true;
  const selectedBoardFootprint = selectedBoard
    ? getBoardFootprint(selectedBoard, selectedBoardScale)
    : null;

  return (
    <Wrapper>
      <Panel>
        <PanelTitle>
          <CircuitBoard size={18} />
          Boards
        </PanelTitle>

        <PanelText>
          Add any board to the canvas to compare its relative Builder size. Unpublished boards can be enabled here and still appear in the public Builder.
        </PanelText>

        <SearchWrap>
          <SearchIcon size={16} />
          <SearchInput
            type="text"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search boards..."
          />
        </SearchWrap>

        {boardsLoading ? (
          <EmptyList>Loading boards...</EmptyList>
        ) : filteredBoards.length === 0 ? (
          <EmptyList>No matching boards found.</EmptyList>
        ) : (
          <BoardList>
            {filteredBoards.map((board) => {
              const boardScale = getDraftScale(board.id);
              const isOnCanvas = canvasItems.some((item) => String(item.boardId) === String(board.id));
              const publicationLabel = Number(board.published) === 1 ? 'Published' : 'Unpublished';

              return (
                <BoardListItem
                  key={board.id}
                  type="button"
                  $active={isOnCanvas}
                  onClick={() => addBoardToCanvas(board)}
                  title={`Add ${board.name} to the Builder canvas`}
                >
                  <BoardThumb>
                    <BoardThumbSvg
                      dangerouslySetInnerHTML={{ __html: getPreparedBoardSvg(board) }}
                    />
                  </BoardThumb>

                  <BoardText>
                    <BoardName>{board.name}</BoardName>
                    <BoardMeta>
                      {[board.manufacturer, board.category].filter(Boolean).join(' • ') || 'Published board'}
                    </BoardMeta>
                    {board.manufacturer || board.category ? (
                      <BoardMeta>{publicationLabel}</BoardMeta>
                    ) : null}
                  </BoardText>

                  <BoardScaleTag>{formatScalePercent(boardScale)}</BoardScaleTag>
                </BoardListItem>
              );
            })}
          </BoardList>
        )}
      </Panel>

      <CanvasPanel>
        <CanvasHeader>
          <div>
            <PanelTitle style={{ marginBottom: 6 }}>
              <Move size={18} />
              Builder Canvas
            </PanelTitle>
            <PanelText style={{ marginBottom: 0 }}>
              Drag boards to compare them visually. Use the inspector to resize the selected board, or hide it from the public Builder picker while keeping its pinout page live.
            </PanelText>
          </div>

          <CanvasMeta>
            <MetaBadge>{canvasItems.length} on canvas</MetaBadge>
            <MetaBadge $tone={dirtyBoardIds.length > 0 ? 'warning' : undefined}>
              {dirtyBoardIds.length} unsaved
            </MetaBadge>
          </CanvasMeta>
        </CanvasHeader>

        <CanvasStage ref={canvasRef}>
          {canvasItems.length === 0 && (
            <EmptyCanvas>
              <EmptyCanvasCard>
                <EmptyTitle>No boards on the canvas yet</EmptyTitle>
                <EmptyText>
                  Choose a board from the list above to start comparing how large it should appear inside Builder.
                </EmptyText>
              </EmptyCanvasCard>
            </EmptyCanvas>
          )}

          {canvasItems.map((item) => {
            const board = getBoardById(item.boardId);
            if (!board) {
              return null;
            }

            const scale = getDraftScale(item.boardId);
            const footprint = getBoardFootprint(board, scale);
            const markup = getPreparedBoardSvg(board);
            const isSelected = selectedCanvasItemId === item.id;

            return (
              <CanvasBoard
                key={item.id}
                type="button"
                $x={item.x}
                $y={item.y}
                $width={footprint.width}
                $height={footprint.height}
                $selected={isSelected}
                onPointerDown={(event) => {
                  if (event.button !== 0) {
                    return;
                  }

                  event.preventDefault();
                  setSelectedCanvasItemId(item.id);

                  dragRef.current = {
                    pointerId: event.pointerId,
                    itemId: item.id,
                    startClientX: event.clientX,
                    startClientY: event.clientY,
                    originX: item.x,
                    originY: item.y,
                    itemWidth: footprint.width,
                    itemHeight: footprint.height,
                  };
                }}
                onClick={() => setSelectedCanvasItemId(item.id)}
              >
                {markup ? (
                  <CanvasBoardSvg dangerouslySetInnerHTML={{ __html: markup }} />
                ) : (
                  <CanvasBoardPlaceholder>
                    {board.name}
                  </CanvasBoardPlaceholder>
                )}
                <CanvasBoardLabel>{board.name}</CanvasBoardLabel>
              </CanvasBoard>
            );
          })}
        </CanvasStage>
      </CanvasPanel>

      <Panel>
        <PanelTitle>
          <Plus size={18} />
          Inspector
        </PanelTitle>

        {!selectedBoard ? (
          <PanelText>
            Select a board on the canvas to adjust its Builder settings and save the new values.
          </PanelText>
        ) : (
          <>
            <InspectorValue>{selectedBoard.name}</InspectorValue>

            <FieldLabel htmlFor="project-builder-enabled">
              Allow to be used in Builder
            </FieldLabel>
            <CheckboxField htmlFor="project-builder-enabled">
              <CheckboxInput
                id="project-builder-enabled"
                type="checkbox"
                checked={selectedBoardEnabled}
                onChange={(event) => updateEnabledDraft(selectedBoard.id, event.target.checked)}
              />
              <div>
                <CheckboxText>
                  {selectedBoardEnabled ? 'Available in Builder' : 'Hidden from Builder'}
                </CheckboxText>
                <CheckboxHint>
                  Disabling this removes the board from the public Builder add-board list, but its public pinout page still stays accessible.
                </CheckboxHint>
              </div>
            </CheckboxField>

            <FieldLabel htmlFor="project-builder-scale-range">
              Builder Size
            </FieldLabel>
            <InspectorValue>{formatScalePercent(selectedBoardScale)}</InspectorValue>
            <RangeInput
              id="project-builder-scale-range"
              type="range"
              min={String(SCALE_MIN)}
              max={String(SCALE_MAX)}
              step={String(SCALE_STEP)}
              value={selectedBoardScale}
              onChange={(event) => updateScaleDraft(selectedBoard.id, event.target.value)}
            />

            <FieldLabel htmlFor="project-builder-scale-number">
              Exact Scale
            </FieldLabel>
            <NumberInput
              id="project-builder-scale-number"
              type="number"
              min={String(SCALE_MIN)}
              max={String(SCALE_MAX)}
              step={String(SCALE_STEP)}
              value={selectedBoardScale}
              onChange={(event) => updateScaleDraft(selectedBoard.id, event.target.value)}
            />

            {selectedBoardFootprint && (
              <InlineInfo>
                Current footprint: {Math.round(selectedBoardFootprint.width)} × {Math.round(selectedBoardFootprint.height)} canvas units.
              </InlineInfo>
            )}

            <ActionRow>
              <Button
                type="button"
                $primary
                onClick={() => saveBoardScale(selectedBoard.id)}
                disabled={
                  savingBoardIds.has(String(selectedBoard.id)) ||
                  !dirtyBoardIds.includes(String(selectedBoard.id))
                }
              >
                <Save size={16} />
                {savingBoardIds.has(String(selectedBoard.id)) ? 'Saving...' : 'Save Settings'}
              </Button>

              <Button
                type="button"
                onClick={() => updateScaleDraft(selectedBoard.id, 1)}
              >
                <RefreshCcw size={16} />
                Reset to 100%
              </Button>
            </ActionRow>

            <ActionRow>
              <Button
                type="button"
                onClick={() => removeCanvasItem(selectedCanvasItemId)}
              >
                <Trash2 size={16} />
                Remove From Canvas
              </Button>
            </ActionRow>
          </>
        )}

        <FieldLabel>Pending Saves</FieldLabel>
        <InlineInfo>
          {dirtyBoardIds.length === 0
            ? 'All Builder settings are saved.'
            : `${dirtyBoardIds.length} board${dirtyBoardIds.length === 1 ? '' : 's'} still have unsaved Builder setting changes.`}
        </InlineInfo>

        <ActionRow>
          <Button
            type="button"
            $primary
            onClick={saveAllDirtyBoards}
            disabled={dirtyBoardIds.length === 0}
          >
            <Save size={16} />
            Save All Pending
          </Button>
        </ActionRow>

        {errorMessage ? (
          <StatusMessage $error>{errorMessage}</StatusMessage>
        ) : null}

        {statusMessage ? (
          <StatusMessage>{statusMessage}</StatusMessage>
        ) : null}
      </Panel>
    </Wrapper>
  );
};

export default ProjectBuilderBoardSizer;
